import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Typography from "@/components/ui/typography";
import { getMentor, Mentor, MentorsKeys } from "@/features/mentors/api/mentors";

interface MentorDetailsDialogProps {
  mentorId: string | null;
  onClose: () => void;
}

const MentorDetailsDialog: React.FC<MentorDetailsDialogProps> = ({
  mentorId,
  onClose,
}) => {
  const { data, isLoading, error } = useQuery<Mentor>({
    queryKey: [MentorsKeys.GET_MENTOR, mentorId],
    queryFn: () => getMentor(mentorId as string),
    enabled: !!mentorId,
  });

  const renderValue = (
    value: any,
    type: "string" | "date" | "link" | "longtext" = "string",
  ) => {
    if (type === "date") {
      if (!value)
        return (
          <Typography size="l2" className="text-muted-foreground">
            Not set
          </Typography>
        );
      return (
        <Typography size="l2">{new Date(value).toLocaleString()}</Typography>
      );
    }
    if (type === "link") {
      if (!value)
        return (
          <Typography size="l2" className="text-muted-foreground">
            Not set
          </Typography>
        );
      return (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline break-all"
        >
          <Typography size="l2" as="span">
            {value}
          </Typography>
        </a>
      );
    }
    if (type === "longtext") {
      if (!value)
        return (
          <Typography size="l2" className="text-muted-foreground">
            Not set
          </Typography>
        );
      return (
        <Typography size="l2" className="whitespace-pre-wrap break-words">
          {value}
        </Typography>
      );
    }
    return (
      <Typography
        size="l2"
        className={!value ? "text-muted-foreground" : undefined}
      >
        {value || "Not set"}
      </Typography>
    );
  };

  const referralLink = data?.referralCode
    ? `https://www.stack.expert/?utm_source=ref&as=${data.referralCode}`
    : undefined;

  return (
    <Dialog open={!!mentorId} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Mentor Details</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-center py-8 text-destructive">
            Failed to load mentor details
          </div>
        )}

        {data && (
          <ScrollArea className="h-[70vh] pr-2">
            <Tabs defaultValue="overview">
              <TabsList className="w-full">
                <TabsTrigger value="overview" className="flex-1">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="links" className="flex-1">
                  Links
                </TabsTrigger>
                <TabsTrigger value="ids" className="flex-1">
                  IDs
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Typography
                        size="l2"
                        className="font-medium text-muted-foreground"
                      >
                        First Name
                      </Typography>
                      <div className="mt-1">{renderValue(data.firstName)}</div>
                    </div>
                    <div>
                      <Typography
                        size="l2"
                        className="font-medium text-muted-foreground"
                      >
                        Last Name
                      </Typography>
                      <div className="mt-1">{renderValue(data.lastName)}</div>
                    </div>
                  </div>
                  <div>
                    <Typography
                      size="l2"
                      className="font-medium text-muted-foreground"
                    >
                      Email
                    </Typography>
                    <div className="mt-1 break-all">
                      {renderValue(data.email)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Typography
                        size="l2"
                        className="font-medium text-muted-foreground"
                      >
                        Referral Code
                      </Typography>
                      <div className="mt-1">
                        {renderValue(data.referralCode)}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Typography
                        size="l2"
                        className="font-medium text-muted-foreground"
                      >
                        Created At
                      </Typography>
                      <div className="mt-1">
                        {renderValue(data.createdAt, "date")}
                      </div>
                    </div>
                    <div>
                      <Typography
                        size="l2"
                        className="font-medium text-muted-foreground"
                      >
                        Updated At
                      </Typography>
                      <div className="mt-1">
                        {renderValue(data.updatedAt, "date")}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="links">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Bio
                    </label>
                    <div className="mt-1">
                      {renderValue(data.bio, "longtext")}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Calendar Link
                    </label>
                    <p className="mt-1">
                      {renderValue(data.calendarLink, "link")}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Referral Link
                    </label>
                    <p className="mt-1">{renderValue(referralLink, "link")}</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="ids">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Internal ID
                    </label>
                    <p className="mt-1 font-mono text-sm break-all">
                      {renderValue(data._id)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Auth0 ID
                    </label>
                    <p className="mt-1 font-mono text-sm break-all">
                      {renderValue(data.auth0Id)}
                    </p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MentorDetailsDialog;
